import { Form, Icon, Message } from "semantic-ui-react";
import TimeInput12h from "../TimeInput12h";
import {
  DEFAULT_ARRIVAL_TIME,
  DEFAULT_DEPARTURE_TIME,
  bookingStayWindow,
  splitStayDateTime,
  toStayDateTime,
} from "../../utils/stayWindow";

/* An Add Room window is usable only when both ends are set and it runs forwards. */
export function isAddRoomWindowValid(window) {
  return Boolean(window?.start && window?.end && window.start < window.end);
}

/* "Rooms needed from … until …" for the Add Room pickers (Booking Detail and
   Front Desk). The window drives both the vacancy list and what each added
   room is sent with (utils/stayWindow addRoomPayloadWindows), so the two can
   never disagree. Controlled: the parent owns `value` and seeds it from
   `addRoomWindow(booking)`.

   `hiddenRooms` are rooms free in the window that the server would still
   refuse — already on this booking at a house that has not opted in to room
   re-use — named here so a missing room is not a mystery. */
export default function AddRoomWindowFields({ booking, value, onChange, hiddenRooms = [] }) {
  const from = splitStayDateTime(value?.start);
  const until = splitStayDateTime(value?.end);
  const bookingStart = bookingStayWindow(booking).start;

  const setFrom = (date, time) =>
    onChange({ ...value, start: toStayDateTime(date, time, DEFAULT_ARRIVAL_TIME) });
  const setUntil = (date, time) =>
    onChange({ ...value, end: toStayDateTime(date, time, DEFAULT_DEPARTURE_TIME) });

  return (
    <div style={{ marginBottom: 12 }}>
      <Form>
        <Form.Group widths="equal" style={{ marginBottom: 6 }}>
          <Form.Input
            label="Rooms needed from"
            type="date"
            value={from.date}
            max={until.date || undefined}
            onChange={(e) => setFrom(e.target.value, from.time)}
          />
          <TimeInput12h
            label="At"
            ariaLabel="Rooms needed from time"
            value={from.time}
            onChange={(t) => setFrom(from.date, t)}
          />
        </Form.Group>
        <Form.Group widths="equal" style={{ marginBottom: 6 }}>
          <Form.Input
            label="Until"
            type="date"
            value={until.date}
            min={from.date || undefined}
            onChange={(e) => setUntil(e.target.value, until.time)}
          />
          <TimeInput12h
            label="At"
            ariaLabel="Rooms needed until time"
            value={until.time}
            onChange={(t) => setUntil(until.date, t)}
          />
        </Form.Group>
      </Form>
      {value?.start && bookingStart && value.start > bookingStart && (
        <p style={{ fontSize: "0.82rem", color: "rgba(0,0,0,0.6)", margin: "0 0 6px" }}>
          Rooms are listed and held from this time, not from the booking's arrival.
        </p>
      )}
      {!isAddRoomWindowValid(value) && (
        <Message warning size="small" style={{ margin: "6px 0" }}>
          <Icon name="warning sign" />
          The "until" date and time must be after the "from" date and time.
        </Message>
      )}
      {hiddenRooms.length > 0 && (
        <Message size="small" style={{ margin: "6px 0" }}>
          <Icon name="info circle" />
          Not listed: Room{hiddenRooms.length > 1 ? "s" : ""}{" "}
          {hiddenRooms.map((r) => r.roomNumber).join(", ")} — already on this booking, and
          room reuse is off for {hiddenRooms.length > 1 ? "their guest houses" : "that guest house"}{" "}
          (Manage → Booking Settings).
        </Message>
      )}
    </div>
  );
}
